import { useState, useEffect, useRef } from 'react'
import { X, Play, Pause, Volume2, VolumeX, Maximize, Minimize, Users } from 'lucide-react'
import Hls from 'hls.js';
import api from '../services/api';
import LoadingShimmer from './LoadingShimmer';
import PtzControls from './PtzControls';
import useStreamManager from '../hooks/useStreamManager';

const StreamViewer = ({ isOpen, onClose, camera }) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [error, setError] = useState(null)
    const [streamUrl, setStreamUrl] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [viewerCount, setViewerCount] = useState(0)
    const videoRef = useRef(null)
    const containerRef = useRef(null)
    const hlsRef = useRef(null)
    
    // Stream manager for sharing integration
    const streamManager = useStreamManager()

    useEffect(() => {
        if (isOpen && camera) {
            startStream();
        }
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [isOpen, camera]);

    const startStream = async () => {
        if (!camera) return;
        setIsLoading(true);
        setError(null);
        try {
            console.log('Starting stream for camera:', camera._id, camera.name);
            const res = await api.get(`/cameras/${camera._id}/stream`);
            console.log('Stream response for camera:', camera.name, res.data);
            if (res.data.data.streamUrl) {
                setStreamUrl(res.data.data.streamUrl);
                
                // Register stream with sharing integration
                streamManager.registerStream(camera._id, {
                    url: res.data.data.streamUrl,
                    type: 'live',
                    userId: 'current_user' // TODO: Get from auth context
                });
                
                console.log('Stream URL set successfully for camera:', camera.name);
            } else {
                throw new Error('No stream URL received');
            }
        } catch (error) {
            console.error('Stream start error for camera:', camera.name, error);
            setError('Connecting to camera...');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (streamUrl && videoRef.current) {
            initializeVideoPlayer();
        }
    }, [streamUrl]);

    const initializeVideoPlayer = () => {
        if (!videoRef.current || !streamUrl) return;
        const absUrl = getAbsoluteStreamUrl(streamUrl);
        console.log('Initializing video player with URL:', absUrl);
        
        if (hlsRef.current) {
            hlsRef.current.destroy();
        }
        
        if (Hls.isSupported()) {
            const hls = new Hls({
                debug: false,
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 30, // Reduced for lower latency
                maxBufferLength: 20, // Reduced for lower latency
                maxMaxBufferLength: 40, // Reduced for lower latency
                maxBufferSize: 100 * 1000 * 1000, // Increased buffer size for high quality
                maxBufferHole: 0.1, // Smaller buffer hole tolerance
                highBufferWatchdogPeriod: 1, // Faster watchdog
                nudgeOffset: 0.1, // Smaller nudge offset
                nudgeMaxRetry: 3, // Fewer retries
                maxFragLookUpTolerance: 0.1, // Smaller tolerance
                liveSyncDurationCount: 2, // Faster sync
                liveMaxLatencyDurationCount: 8, // Lower latency
                fragLoadingMaxRetry: 2, // Fewer retries
                manifestLoadingMaxRetry: 2, // Fewer retries
                levelLoadingMaxRetry: 2 // Fewer retries
            });
            hlsRef.current = hls;
            hls.loadSource(absUrl);
            hls.attachMedia(videoRef.current);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('Stream manifest loaded successfully for camera:', camera.name);
                videoRef.current.play().catch((error) => {
                    console.log('Autoplay failed, but video will still load:', error);
                });
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Error for camera:', camera.name, data);
                if (data.type === 'networkError' && data.details === 'manifestLoadError') {
                    setError('Camera is starting up...');
                } else if (data.type === 'mediaError' && (data.details === 'bufferStalledError' || data.details === 'bufferSeekOverHole')) {
                    return;
                } else if (data.fatal) {
                    setError('Optimizing connection...');
                }
            });
            videoRef.current.onerror = (error) => {
                console.error('Video error occurred for camera:', camera.name, error);
                setError('Adjusting video quality...');
            };
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = absUrl;
            videoRef.current.play().catch((error) => {
                console.log('Autoplay failed for Safari, but video will still load:', error);
            });
        } else {
            setError('Updating video player...');
        }
    };

    const handlePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const handleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
        }
    }

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen()
            setIsFullscreen(true)
        } else {
            document.exitFullscreen()
            setIsFullscreen(false)
        }
    }

    const handleClose = () => {
        if (videoRef.current) {
            videoRef.current.pause()
        }
        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }
        setIsPlaying(false)
        setError(null)
        setStreamUrl(null)
        setIsLoading(false)
        onClose()
    }

    // Helper to get absolute stream URL
    const getAbsoluteStreamUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        // Use environment variable for stream URL or fallback to API URL
        const baseUrl = import.meta.env.VITE_API_STREAM_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3667';
        return baseUrl + url;
    };

    if (!isOpen || !camera) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div
                ref={containerRef}
                className="bg-black rounded-lg overflow-hidden w-full max-w-4xl mx-4"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
                    <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <h2 className="text-lg font-semibold">{camera.name}</h2>
                        <span className="text-sm text-gray-400">
                            {camera.ipAddress}:{camera.port}
                        </span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleMute}
                            className="p-2 hover:bg-gray-800 rounded"
                            title={isMuted ? 'Unmute' : 'Mute'}
                        >
                            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </button>
                        <button
                            onClick={handleFullscreen}
                            className="p-2 hover:bg-gray-800 rounded"
                            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                        </button>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-800 rounded"
                            title="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Viewer Count */}
                {viewerCount > 1 && (
                    <div className="absolute top-16 right-4 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded flex items-center space-x-2 z-30">
                        <Users className="h-4 w-4" />
                        <span>{viewerCount} viewers</span>
                    </div>
                )}

                {/* Video Container */}
                <div className="relative bg-black">
                    <PtzControls camera={camera} />
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                        {(isLoading || error) ? (
                            <div className="relative w-full h-full">
                                <LoadingShimmer />
                                {error && (
                                    <div className="absolute bottom-4 right-4">
                                        <button
                                            onClick={startStream}
                                            className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors backdrop-blur-sm"
                                        >
                                            Refresh
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : streamUrl ? (
                            <video
                                ref={videoRef}
                                className="w-full h-full bg-black rounded"
                                controls
                                autoPlay
                                muted={isMuted}
                                style={{ display: 'block' }}
                                onPlay={() => {
                                    setIsPlaying(true);
                                    // Update viewer count
                                    const currentViewerCount = streamManager.getViewerCount(camera._id);
                                    setViewerCount(currentViewerCount);
                                }}
                                onPause={() => setIsPlaying(false)}
                                onError={() => setError('Adjusting video quality...')}
                            />
                        ) : (
                            <div className="text-center text-white">
                                <div className="text-6xl mb-4">📹</div>
                                <h3 className="text-xl font-semibold mb-2">Camera Stream</h3>
                                <p className="text-gray-400 mb-4">
                                    {camera.type?.toUpperCase()} Stream from {camera.name}
                                </p>
                                <div className="space-y-2 text-sm text-gray-400">
                                    <p>Resolution: {camera.resolution?.width}x{camera.resolution?.height}</p>
                                    <p>FPS: {camera.fps}</p>
                                    <p>Bitrate: {camera.bitrate} kbps</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Controls */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                        <div className="flex items-center space-x-4 bg-black bg-opacity-50 rounded-full px-6 py-3">
                            <button
                                onClick={handlePlay}
                                className="text-white hover:text-gray-300"
                                title={isPlaying ? 'Pause' : 'Play'}
                            >
                                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Camera Info */}
                <div className="p-4 bg-gray-900 text-white">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Type:</span>
                            <span className="ml-2">{camera.type.toUpperCase()}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Location:</span>
                            <span className="ml-2">{camera.location || 'Not specified'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Status:</span>
                            <span className={`ml-2 ${camera.isOnline ? 'text-green-400' : 'text-red-400'}`}>
                                {camera.isOnline ? 'Online' : 'Offline'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400">Last Seen:</span>
                            <span className="ml-2">
                                {camera.lastSeen ? new Date(camera.lastSeen).toLocaleString() : 'Never'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StreamViewer 